/**
 * React hooks for css-motion
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { initObserver, getVarsObject, getOnLoadClass } from '../core';
import type { AnimationConfig, IntersectionObserverOptions } from '../core';

/**
 * React hook that combines animation styles and intersection observer
 * Returns the element ref and computed styles
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { ref, style, className, isInView } = useAnimateOnView(
 *     presets.slideUp()
 *   );
 *   return <div ref={ref} style={style} className={className}>...</div>;
 * }
 * ```
 */
export function useAnimateOnView(
  animation: AnimationConfig,
  options: IntersectionObserverOptions = {}
): {
  ref: React.RefObject<HTMLDivElement | null>;
  style: React.CSSProperties;
  className: string;
  isInView: boolean;
} {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const { threshold = 0.1, rootMargin = '0px', once = true } = options;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsInView(true);
            element.classList.add('in-view');
            if (once) {
              observer.unobserve(element);
            }
          } else if (!once) {
            setIsInView(false);
            element.classList.remove('in-view');
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [options.threshold, options.rootMargin, options.once]);

  const style = getVarsObject(animation);

  return {
    ref,
    style,
    className: 'css-motion css-motion--view',
    isInView,
  };
}

/**
 * React hook for initializing global animations
 * Call this once in your app root component
 *
 * @example
 * ```tsx
 * function App() {
 *   useInitAnimations();
 *   return <div>...</div>;
 * }
 * ```
 */
export function useInitAnimations(options: IntersectionObserverOptions = {}) {
  useEffect(() => {
    const cleanup = initObserver(options);
    return cleanup.destroy;
  }, [options.threshold, options.rootMargin, options.once]);
}

/**
 * React hook for load-triggered animations
 * Returns animation styles and class name for immediate animations
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { style, className } = useAnimateOnLoad(presets.fadeIn());
 *   return <div style={style} className={className}>...</div>;
 * }
 * ```
 */
export function useAnimateOnLoad(
  animation: AnimationConfig,
  externalClassName = ''
): { style: React.CSSProperties; className: string } {
  const style = useMemo(() => getVarsObject(animation), [animation]);
  const className = useMemo(
    () => getOnLoadClass(externalClassName),
    [externalClassName]
  );

  return {
    style,
    className,
  };
}
